import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { pickLang, type GameObject, type GameStateResponse } from "@shared";
import { currentLang } from "../i18n";
import MissionTimer from "./MissionTimer";
import { ordreSeme } from "./shuffle";

interface Props {
  /** Titre de la mission, tel que l'admin l'a écrit. */
  title: string;
  state: GameStateResponse;
  justFound: string | null;
  busy: boolean;
  errorKey: string | null;
  /** Le jeune a voulu cocher plus d'objets qu'il n'y en a à trouver. */
  limitReached: boolean;
  onCloseLimit: () => void;
  onToggle: (object: GameObject) => void;
  onFinish: () => void;
}

/**
 * Mission 1 en cours, mode Aventurier.
 *
 * Le jeune repère l'objet physique autour de lui et touche la carte
 * correspondante : elle tombe dans son sac. Aucun message « bravo » ou
 * « raté » — cocher est un geste qu'il maîtrise, il n'y a rien à valider.
 * L'animation et le son sont là pour le plaisir.
 */
export default function Mission1Screen({
  title,
  state,
  justFound,
  busy,
  errorKey,
  limitReached,
  onCloseLimit,
  onToggle,
  onFinish,
}: Props) {
  const { t } = useTranslation();
  const lang = currentLang();

  // La progression se mesure sur les objets À TROUVER, pas sur les vignettes
  // affichées : la grille en contient davantage, des leurres mêlés aux vrais.
  //
  // Et elle compte les COCHES, pas les bonnes réponses. Une barre qui ne
  // bougerait pas en cochant un leurre le désignerait aussitôt : il suffirait
  // de cocher un par un en regardant le compteur. Ici le jeune voit ce qu'il a
  // fait ; ce qu'il a réussi, il l'apprend au bilan.
  const aTrouver = state.objectsToFind;

  // Ordre propre à CETTE partie. Il se recalcule à chaque coche — `state` change
  // alors — mais la graine ne bouge pas, donc la grille reste identique. C'est
  // exactement ce qu'on veut : rien ne doit bouger sous les doigts du jeune.
  const missionId = state.mission?.id ?? "";
  const objets = useMemo(
    () => ordreSeme(state.objects, missionId, (objet) => objet.id),
    [state.objects, missionId],
  );
  const percent = aTrouver === 0 ? 0 : Math.min(100, Math.round((state.found / aTrouver) * 100));
  const allFound = aTrouver > 0 && state.found >= aTrouver;

  return (
    <main className="screen screen--game">
      <div className="hud">
        <div className="hud__row">
          <span className="hud__count">
            {state.found} / {aTrouver}
          </span>
          <h1 className="hud__title">{title}</h1>
          {state.mission !== null && <MissionTimer startedAt={state.mission.startedAt} />}
        </div>
        <div
          className="progress"
          role="progressbar"
          aria-valuenow={percent}
          aria-valuemin={0}
          aria-valuemax={100}
        >
          <div className="progress__bar" style={{ width: `${percent}%` }} />
        </div>
      </div>

      {errorKey !== null && <p className="form__error">{t(errorKey)}</p>}

      <ul className="objects">
        {objets.map((object) => (
          <li key={object.id}>
            <button
              type="button"
              className={`object ${object.found ? "object--found" : ""} ${
                justFound === object.id ? "object--stamping" : ""
              }`}
              aria-pressed={object.found}
              onClick={() => {
                onToggle(object);
              }}
            >
              <span className="object__image">
                {object.imageUrl !== null && (
                  <img src={object.imageUrl} alt="" loading="lazy" draggable={false} />
                )}
                {object.found && (
                  <span className="object__stamp" aria-hidden="true">
                    ✓
                  </span>
                )}
              </span>
              <span className="object__name">{pickLang(object.nameI18n, lang)}</span>
            </button>
          </li>
        ))}
      </ul>

      <div className="game__actions">
        {allFound && <p className="game__nudge">{t("game.allFound", { count: aTrouver })}</p>}
        <button
          type="button"
          className={`button button--full ${allFound ? "button--primary" : "button--ghost"}`}
          disabled={busy}
          onClick={onFinish}
        >
          {t("game.finish", { number: 1 })}
        </button>
      </div>

      {limitReached && (
        <div className="dialog__backdrop" role="presentation" onClick={onCloseLimit}>
          <div
            className="dialog"
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="limit-title"
            onClick={(e) => {
              e.stopPropagation();
            }}
          >
            <h3 className="dialog__title" id="limit-title">
              {t("game.limit.title", { count: aTrouver })}
            </h3>
            <p className="card__text">{t("game.limit.text", { count: aTrouver })}</p>
            <button
              type="button"
              className="button button--primary button--full"
              autoFocus
              onClick={onCloseLimit}
            >
              {t("game.limit.ok")}
            </button>
          </div>
        </div>
      )}
    </main>
  );
}
